import { useState, useEffect, useRef, useCallback } from 'react';
import { adminAPI } from '../lib/api';
import {
  Loader2, Radio, Car, Package, MapPin, Clock, Users, DollarSign,
  AlertCircle, CheckCircle, Navigation, Route, Zap, RefreshCw, X
} from 'lucide-react';
import { Button } from './ui/button';
import { toast } from 'sonner';
import { devError } from '../lib/devLog';

const MAPBOX_TOKEN = process.env.REACT_APP_BACKEND_URL ? process.env.REACT_APP_MAPBOX_TOKEN : '';
const REFRESH_INTERVAL = 15000;

const STATUS_COLORS = {
  pending: '#eab308',
  assigned: '#3b82f6',
  accepted: '#8b5cf6',
  in_progress: '#f97316',
};

const ControlTower = () => {
  const mapContainer = useRef(null);
  const map = useRef(null);
  const markersRef = useRef([]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mapReady, setMapReady] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [lastRefresh, setLastRefresh] = useState(null);
  const [autoRefresh, setAutoRefresh] = useState(true);

  const fetchData = useCallback(async () => {
    try {
      const res = await adminAPI.getControlTower();
      setData(res.data);
      setLastRefresh(new Date());
    } catch (err) {
      devError('Control tower fetch error:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  // Initial load + auto-refresh
  useEffect(() => {
    fetchData();
    let interval;
    if (autoRefresh) {
      interval = setInterval(fetchData, REFRESH_INTERVAL);
    }
    return () => { if (interval) clearInterval(interval); };
  }, [fetchData, autoRefresh]);

  // Load Mapbox CDN
  useEffect(() => {
    if (window.mapboxgl) { setMapReady(true); return; }
    const script = document.createElement('script');
    script.src = 'https://api.mapbox.com/mapbox-gl-js/v3.0.1/mapbox-gl.js';
    script.onload = () => {
      const link = document.createElement('link');
      link.href = 'https://api.mapbox.com/mapbox-gl-js/v3.0.1/mapbox-gl.css';
      link.rel = 'stylesheet';
      document.head.appendChild(link);
      setMapReady(true);
    };
    document.head.appendChild(script);
  }, []);

  // Initialize map
  useEffect(() => {
    if (!mapReady || !data || !mapContainer.current || map.current) return;

    window.mapboxgl.accessToken = MAPBOX_TOKEN || process.env.REACT_APP_MAPBOX_TOKEN;
    map.current = new window.mapboxgl.Map({
      container: mapContainer.current,
      style: 'mapbox://styles/mapbox/dark-v11',
      center: [data.hub.lon, data.hub.lat],
      zoom: 10,
    });

    map.current.on('load', () => {
      map.current.addControl(new window.mapboxgl.NavigationControl(), 'top-right');
      renderMapLayers();
    });

    return () => {
      if (map.current) { map.current.remove(); map.current = null; }
    };
  }, [mapReady, data]);

  // Update markers when data refreshes
  useEffect(() => {
    if (map.current && data && map.current.loaded()) {
      renderMapLayers();
    }
  }, [data]);

  const clearMarkers = () => {
    markersRef.current.forEach(m => m.remove());
    markersRef.current = [];
  };

  const renderMapLayers = () => {
    if (!map.current || !data) return;
    clearMarkers();

    // Hub markers (all active hubs)
    const allHubs = (data.hubs && data.hubs.length > 0)
      ? data.hubs
      : [{ name: data.hub.name, lat: data.hub.lat, lon: data.hub.lon, zones: data.zones, is_primary: true }];

    allHubs.forEach(hub => {
      const isPrimary = !!hub.is_primary;
      const hubEl = document.createElement('div');
      hubEl.style.cssText = `width:${isPrimary ? 36 : 28}px;height:${isPrimary ? 36 : 28}px;border-radius:50%;background:${isPrimary ? '#f97316' : '#8b5cf6'};border:3px solid white;box-shadow:0 2px 8px rgba(0,0,0,0.4);display:flex;align-items:center;justify-content:center;cursor:pointer;`;
      const labelSpan = document.createElement('span');
      labelSpan.style.cssText = `color:white;font-weight:bold;font-size:${isPrimary ? 11 : 9}px;`;
      labelSpan.textContent = 'HUB';
      hubEl.appendChild(labelSpan);

      const popupNode = document.createElement('div');
      popupNode.style.cssText = 'font-size:13px;font-weight:600;';
      popupNode.textContent = `${hub.name} Hub${isPrimary ? ' (Primary)' : ''}`;
      const popup = new window.mapboxgl.Popup({ offset: 20 }).setDOMContent(popupNode);

      const hubMarker = new window.mapboxgl.Marker(hubEl)
        .setLngLat([hub.lon ?? hub.lng, hub.lat])
        .setPopup(popup)
        .addTo(map.current);
      markersRef.current.push(hubMarker);
    });

    // Zone rings (for each hub)
    if (!map.current.getSource('zones')) {
      const allRings = [];
      allHubs.forEach((hub, hubIdx) => {
        const zones = hub.zones && hub.zones.length ? hub.zones : data.zones;
        zones.forEach(z => {
          const points = 64;
          const coords = [];
          const radiusKm = (z.radius_miles || z.max_distance || 0) * 1.60934;
          const cLat = hub.lat;
          const cLon = hub.lon ?? hub.lng;
          for (let i = 0; i <= points; i++) {
            const angle = (i / points) * Math.PI * 2;
            const dx = radiusKm * Math.cos(angle);
            const dy = radiusKm * Math.sin(angle);
            const lat = cLat + (dy / 111.32);
            const lon = cLon + (dx / (111.32 * Math.cos(cLat * Math.PI / 180)));
            coords.push([lon, lat]);
          }
          allRings.push({
            type: 'Feature',
            properties: { name: z.name, hub: hub.name, primary: !!hub.is_primary },
            geometry: { type: 'LineString', coordinates: coords }
          });
        });
      });
      map.current.addSource('zones', { type: 'geojson', data: { type: 'FeatureCollection', features: allRings } });
      map.current.addLayer({
        id: 'zone-rings',
        type: 'line',
        source: 'zones',
        paint: {
          'line-color': ['case', ['get', 'primary'], '#f97316', '#8b5cf6'],
          'line-width': 1,
          'line-opacity': ['case', ['get', 'primary'], 0.4, 0.25],
          'line-dasharray': [4, 4]
        }
      });
    }

    // Job markers with route lines
    data.jobs.forEach(job => {
      if (!job.pickup_lat) return;

      // Pickup marker
      const pickupEl = document.createElement('div');
      const color = STATUS_COLORS[job.status] || '#6b7280';
      pickupEl.style.cssText = `width:14px;height:14px;border-radius:50%;background:${color};border:2px solid white;box-shadow:0 1px 4px rgba(0,0,0,0.3);cursor:pointer;`;
      const pickupMarker = new window.mapboxgl.Marker(pickupEl)
        .setLngLat([job.pickup_lon, job.pickup_lat])
        .addTo(map.current);
      pickupEl.addEventListener('click', () => setSelectedItem({ type: 'job', data: job }));
      markersRef.current.push(pickupMarker);

      // Route line to dropoff
      if (job.dropoff_lat) {
        const lineId = `job-line-${job.id}`;
        if (!map.current.getSource(lineId)) {
          map.current.addSource(lineId, {
            type: 'geojson',
            data: { type: 'Feature', geometry: { type: 'LineString', coordinates: [[job.pickup_lon, job.pickup_lat], [job.dropoff_lon, job.dropoff_lat]] } }
          });
          map.current.addLayer({
            id: lineId, type: 'line', source: lineId,
            paint: { 'line-color': color, 'line-width': 2.5, 'line-opacity': 0.7 }
          });
        }
      }
    });

    // Driver markers
    data.drivers.forEach(driver => {
      if (!driver.lat) return;
      const el = document.createElement('div');
      const isActive = driver.has_active_job;
      const isIdle = driver.is_idle;
      const bgColor = isActive ? '#22c55e' : isIdle ? '#6366f1' : '#3b82f6';
      el.style.cssText = `width:24px;height:24px;border-radius:50%;background:${bgColor};border:2px solid white;box-shadow:0 2px 6px rgba(0,0,0,0.3);display:flex;align-items:center;justify-content:center;cursor:pointer;`;
      el.textContent = driver.name?.charAt(0) || 'D';
      el.style.fontSize = '10px';
      el.style.color = 'white';
      el.style.fontWeight = 'bold';
      if (isActive) {
        el.style.animation = 'pulse 2s infinite';
      }
      const marker = new window.mapboxgl.Marker(el)
        .setLngLat([driver.lon, driver.lat])
        .addTo(map.current);
      el.addEventListener('click', () => setSelectedItem({ type: 'driver', data: driver }));
      markersRef.current.push(marker);
    });

    // Route lines (multi-stop)
    data.routes.forEach(route => {
      const stops = route.stops?.filter(s => s.lat != null && s.lon != null);
      if (!stops || stops.length < 2) return;
      const lineId = `route-line-${route.id}`;
      if (map.current.getSource(lineId)) {
        map.current.removeLayer(lineId);
        map.current.removeSource(lineId);
      }
      map.current.addSource(lineId, {
        type: 'geojson',
        data: { type: 'Feature', geometry: { type: 'LineString', coordinates: stops.map(s => [s.lon, s.lat]) } }
      });
      map.current.addLayer({
        id: lineId, type: 'line', source: lineId,
        paint: { 'line-color': '#a855f7', 'line-width': 2, 'line-dasharray': [3, 2], 'line-opacity': 0.6 }
      });

      // First stop marker for route
      const startEl = document.createElement('div');
      startEl.style.cssText = 'width:10px;height:10px;border-radius:50%;background:#a855f7;border:2px solid white;box-shadow:0 1px 3px rgba(0,0,0,0.3);cursor:pointer;';
      const startMarker = new window.mapboxgl.Marker(startEl)
        .setLngLat([stops[0].lon, stops[0].lat])
        .addTo(map.current);
      startEl.addEventListener('click', () => setSelectedItem({ type: 'route', data: route }));
      markersRef.current.push(startMarker);
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-8 h-8 animate-spin text-orange-500" />
      </div>
    );
  }

  const stats = data?.live_stats || {};

  return (
    <div className="space-y-0" data-testid="control-tower">
      {/* Control Bar */}
      <div className="flex items-center justify-between bg-zinc-900 text-white px-4 py-2.5 rounded-t-xl">
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-1.5">
            <Radio className="w-4 h-4 text-green-400 animate-pulse" />
            <span className="text-sm font-semibold tracking-wide">CONTROL TOWER</span>
          </div>
          <span className="text-xs text-zinc-400">
            {lastRefresh ? `Updated ${lastRefresh.toLocaleTimeString()}` : ''}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setAutoRefresh(!autoRefresh)}
            className={`text-xs px-2 py-1 rounded ${autoRefresh ? 'bg-green-600 text-white' : 'bg-zinc-700 text-zinc-300'}`}
            data-testid="auto-refresh-toggle"
          >
            {autoRefresh ? 'LIVE' : 'PAUSED'}
          </button>
          <Button variant="ghost" size="sm" className="h-7 text-white hover:bg-zinc-800" onClick={fetchData}>
            <RefreshCw className="w-3.5 h-3.5" />
          </Button>
        </div>
      </div>

      <div className="flex flex-col sm:flex-row rounded-b-xl overflow-hidden border border-zinc-200 border-t-0" style={{ height: '520px' }}>
        {/* Map */}
        <div className="flex-1 relative" style={{ minHeight: '280px' }}>
          <div ref={mapContainer} style={{ width: '100%', height: '100%' }} />

          {/* Legend */}
          <div className="absolute bottom-3 left-3 bg-zinc-900/90 text-white rounded-lg px-3 py-2 text-xs space-y-1.5 backdrop-blur-sm">
            <div className="flex items-center gap-2"><div className="w-3 h-3 rounded-full bg-yellow-500" /> Pending</div>
            <div className="flex items-center gap-2"><div className="w-3 h-3 rounded-full bg-blue-500" /> Assigned</div>
            <div className="flex items-center gap-2"><div className="w-3 h-3 rounded-full bg-purple-500" /> Accepted</div>
            <div className="flex items-center gap-2"><div className="w-3 h-3 rounded-full bg-orange-500" /> In Progress</div>
            <div className="flex items-center gap-2"><div className="w-3 h-3 rounded-full bg-green-500" /> Driver Active</div>
            <div className="flex items-center gap-2"><div className="w-3 h-3 rounded-full bg-blue-500" /> Driver Available</div>
            <div className="flex items-center gap-2"><div className="w-3 h-3 rounded-full bg-indigo-500" /> Driver Idle</div>
            <div className="flex items-center gap-2"><div className="w-2 h-0.5 bg-purple-400" style={{width:'12px'}} /> Route</div>
          </div>
        </div>

        {/* Sidebar */}
        <div className="w-full sm:w-80 bg-zinc-50 border-t sm:border-t-0 sm:border-l border-zinc-200 flex flex-col overflow-hidden" style={{ maxHeight: '420px', minHeight: '320px' }}>
          {/* Live Stats */}
          <div className="grid grid-cols-2 gap-0 border-b border-zinc-200">
            <StatCard icon={<Zap className="w-4 h-4 text-green-500" />} value={stats.drivers_online} label="Online" />
            <StatCard icon={<Navigation className="w-4 h-4 text-orange-500" />} value={stats.active_jobs} label="Active" />
            <StatCard icon={<Clock className="w-4 h-4 text-yellow-500" />} value={stats.pending_jobs} label="Pending" />
            <StatCard icon={<CheckCircle className="w-4 h-4 text-green-500" />} value={stats.today_completed} label="Done Today" />
          </div>

          {/* Revenue */}
          <div className="px-3 py-2 border-b border-zinc-200 bg-white flex items-center justify-between">
            <span className="text-xs text-zinc-500">Today's Revenue</span>
            <span className="text-sm font-bold text-green-600">${stats.today_revenue?.toFixed(2) || '0.00'}</span>
          </div>

          {/* Per-Hub Heatmap */}
          {data?.hub_heatmap?.length > 0 && (
            <div className="px-3 py-2 border-b border-zinc-200 bg-white" data-testid="hub-heatmap">
              <p className="text-xs font-semibold text-zinc-500 uppercase tracking-wider mb-1.5">Hub Activity</p>
              <div className="space-y-1">
                {data.hub_heatmap.map(h => {
                  const total = (h.active_jobs || 0) + (h.pending_jobs || 0);
                  // max for normalization
                  const maxTotal = Math.max(...data.hub_heatmap.map(x => (x.active_jobs || 0) + (x.pending_jobs || 0)), 1);
                  const heat = Math.round((total / maxTotal) * 100);
                  // bar color: green low, orange mid, red high
                  const bgColor = heat === 0
                    ? 'bg-zinc-100'
                    : heat < 33 ? 'bg-green-200'
                    : heat < 67 ? 'bg-orange-300'
                    : 'bg-red-400';
                  return (
                    <div key={h.id} className="flex items-center gap-2 text-xs" data-testid={`hub-heat-${h.id}`}>
                      <span className={`w-1.5 h-1.5 rounded-full ${h.is_primary ? 'bg-orange-500' : 'bg-violet-500'} flex-shrink-0`} />
                      <span className="font-medium text-zinc-700 truncate w-16 flex-shrink-0">{h.name}</span>
                      <div className="flex-1 bg-zinc-100 rounded-full h-2 overflow-hidden">
                        <div
                          className={`h-full ${bgColor} transition-all`}
                          style={{ width: `${Math.max(heat, total > 0 ? 8 : 0)}%` }}
                          title={`${total} jobs (${h.active_jobs} active, ${h.pending_jobs} pending) · ${h.online_drivers} drivers`}
                        />
                      </div>
                      <span className="text-zinc-500 flex-shrink-0 w-14 text-right tabular-nums">
                        {total > 0 ? `${total} job${total === 1 ? '' : 's'}` : <span className="text-zinc-300">—</span>}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Detail panel or lists */}
          <div className="flex-1 overflow-y-auto">
            {selectedItem ? (
              <DetailPanel item={selectedItem} onClose={() => setSelectedItem(null)} />
            ) : (
              <div className="p-3 space-y-3">
                {/* Online Drivers */}
                <div>
                  <h4 className="text-xs font-semibold text-zinc-500 uppercase tracking-wider mb-2">Online Drivers ({data?.drivers?.length || 0})</h4>
                  {data?.drivers?.length === 0 && <p className="text-xs text-zinc-400">No drivers online</p>}
                  {data?.drivers?.map(d => (
                    <button
                      key={d.id}
                      onClick={() => setSelectedItem({ type: 'driver', data: d })}
                      className="w-full text-left flex items-center gap-2 p-2 rounded-lg hover:bg-white transition-colors mb-1"
                    >
                      <div className={`w-7 h-7 rounded-full ${d.has_active_job ? 'bg-green-500' : d.is_idle ? 'bg-indigo-500' : 'bg-blue-500'} text-white text-xs font-bold flex items-center justify-center`}>
                        {d.name?.charAt(0)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-zinc-800 truncate">{d.name}</p>
                        <p className="text-xs text-zinc-400">{d.vehicle_type} {d.has_active_job ? '• On job' : d.is_idle ? '• Idle' : '• Available'}</p>
                      </div>
                      {d.lat && <div className="w-2 h-2 rounded-full bg-green-400" />}
                    </button>
                  ))}
                </div>

                {/* Active Jobs */}
                <div>
                  <h4 className="text-xs font-semibold text-zinc-500 uppercase tracking-wider mb-2">Active Jobs ({data?.jobs?.length || 0})</h4>
                  {data?.jobs?.length === 0 && <p className="text-xs text-zinc-400">No active jobs</p>}
                  {data?.jobs?.map(j => (
                    <button
                      key={j.id}
                      onClick={() => setSelectedItem({ type: 'job', data: j })}
                      className="w-full text-left flex items-center gap-2 p-2 rounded-lg hover:bg-white transition-colors mb-1"
                    >
                      <div className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ background: (STATUS_COLORS[j.status] || '#6b7280') + '22' }}>
                        {j.job_type === 'ride' ? <Car className="w-3.5 h-3.5" style={{ color: STATUS_COLORS[j.status] }} /> : <Package className="w-3.5 h-3.5" style={{ color: STATUS_COLORS[j.status] }} />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-zinc-800 truncate">{j.pickup_address?.split(',')[0]}</p>
                        <p className="text-xs text-zinc-400">{j.status} • ${j.price?.toFixed(2)}</p>
                      </div>
                    </button>
                  ))}
                </div>

                {/* Routes */}
                <div>
                  <h4 className="text-xs font-semibold text-zinc-500 uppercase tracking-wider mb-2">Driver Routes ({data?.routes?.length || 0})</h4>
                  {data?.routes?.length === 0 && <p className="text-xs text-zinc-400">No active routes</p>}
                  {data?.routes?.map(r => (
                    <button
                      key={r.id}
                      onClick={() => setSelectedItem({ type: 'route', data: r })}
                      className="w-full text-left flex items-center gap-2 p-2 rounded-lg hover:bg-white transition-colors mb-1"
                    >
                      <div className="w-7 h-7 rounded-lg bg-purple-100 flex items-center justify-center">
                        <Route className="w-3.5 h-3.5 text-purple-600" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-zinc-800 truncate">{r.route_name}</p>
                        <p className="text-xs text-zinc-400">{r.driver_name} • {r.stops?.length || 2} stops</p>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const StatCard = ({ icon, value, label }) => (
  <div className="flex items-center gap-2 px-3 py-2.5 bg-white border-b border-r border-zinc-100">
    {icon}
    <div>
      <p className="text-lg font-bold text-zinc-900 leading-none">{value || 0}</p>
      <p className="text-xs text-zinc-400">{label}</p>
    </div>
  </div>
);

const DetailPanel = ({ item, onClose }) => {
  const { type, data } = item;

  return (
    <div className="p-3">
      <div className="flex items-center justify-between mb-3">
        <span className="text-xs font-semibold text-zinc-500 uppercase tracking-wider">
          {type === 'job' ? 'Job Details' : type === 'driver' ? 'Driver Details' : 'Route Details'}
        </span>
        <button onClick={onClose} className="text-zinc-400 hover:text-zinc-600">
          <X className="w-4 h-4" />
        </button>
      </div>

      {type === 'job' && (
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            {data.job_type === 'ride'
              ? <Car className="w-5 h-5 text-orange-500" />
              : <Package className="w-5 h-5 text-orange-500" />}
            <span className="font-semibold text-zinc-900 capitalize">{data.job_type}</span>
            <span className="text-xs px-2 py-0.5 rounded-full font-medium" style={{ background: (STATUS_COLORS[data.status] || '#6b7280') + '22', color: STATUS_COLORS[data.status] }}>{data.status}</span>
          </div>
          <div className="bg-white rounded-lg p-3 space-y-2 border border-zinc-100">
            <div className="flex items-start gap-2 text-sm">
              <div className="w-2 h-2 rounded-full bg-green-500 mt-1.5 flex-shrink-0" />
              <span className="text-zinc-700">{data.pickup_address}</span>
            </div>
            <div className="flex items-start gap-2 text-sm">
              <div className="w-2 h-2 rounded-full bg-red-500 mt-1.5 flex-shrink-0" />
              <span className="text-zinc-700">{data.dropoff_address}</span>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2 text-sm">
            <div className="bg-white rounded-lg p-2 border border-zinc-100">
              <p className="text-xs text-zinc-400">Price</p>
              <p className="font-bold text-zinc-900">${data.price?.toFixed(2)}</p>
            </div>
            <div className="bg-white rounded-lg p-2 border border-zinc-100">
              <p className="text-xs text-zinc-400">Zone</p>
              <p className="font-semibold text-zinc-900">{data.zone_name || 'Hub'}</p>
            </div>
            <div className="bg-white rounded-lg p-2 border border-zinc-100">
              <p className="text-xs text-zinc-400">Customer</p>
              <p className="font-semibold text-zinc-900 truncate">{data.user_name || '—'}</p>
            </div>
            <div className="bg-white rounded-lg p-2 border border-zinc-100">
              <p className="text-xs text-zinc-400">Driver</p>
              <p className="font-semibold text-zinc-900 truncate">{data.driver_name || 'Unassigned'}</p>
            </div>
          </div>
        </div>
      )}

      {type === 'driver' && (
        <div className="space-y-3">
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-full ${data.has_active_job ? 'bg-green-500' : 'bg-blue-500'} text-white text-lg font-bold flex items-center justify-center`}>
              {data.name?.charAt(0)}
            </div>
            <div>
              <p className="font-semibold text-zinc-900">{data.name}</p>
              <p className="text-sm text-zinc-500">{data.email}</p>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2 text-sm">
            <div className="bg-white rounded-lg p-2 border border-zinc-100">
              <p className="text-xs text-zinc-400">Vehicle</p>
              <p className="font-semibold text-zinc-900 capitalize">{data.vehicle_type || 'Car'}</p>
            </div>
            <div className="bg-white rounded-lg p-2 border border-zinc-100">
              <p className="text-xs text-zinc-400">Status</p>
              <p className={`font-semibold ${data.has_active_job ? 'text-green-600' : 'text-blue-600'}`}>
                {data.has_active_job ? 'On Job' : 'Available'}
              </p>
            </div>
            <div className="bg-white rounded-lg p-2 border border-zinc-100 col-span-2">
              <p className="text-xs text-zinc-400">GPS</p>
              <p className="font-semibold text-zinc-900">
                {data.lat ? `${data.lat.toFixed(4)}, ${data.lon.toFixed(4)}` : 'No location data'}
              </p>
            </div>
          </div>
        </div>
      )}

      {type === 'route' && (
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <Route className="w-5 h-5 text-purple-500" />
            <span className="font-semibold text-zinc-900">{data.route_name}</span>
          </div>
          <p className="text-sm text-zinc-500">Driver: {data.driver_name}</p>
          <div className="bg-white rounded-lg p-3 space-y-1.5 border border-zinc-100">
            {(data.stops || []).map((stop, i) => (
              <div key={`${stop.address}-${i}`} className="flex items-start gap-2 text-sm">
                <div className={`w-5 h-5 rounded-full text-white text-xs font-bold flex items-center justify-center flex-shrink-0 ${i === 0 ? 'bg-green-500' : i === (data.stops?.length || 1) - 1 ? 'bg-red-500' : 'bg-purple-400'}`}>
                  {String.fromCharCode(65 + i)}
                </div>
                <span className="text-zinc-700">{stop.address}</span>
              </div>
            ))}
          </div>
          <div className="grid grid-cols-2 gap-2 text-sm">
            <div className="bg-white rounded-lg p-2 border border-zinc-100">
              <p className="text-xs text-zinc-400">Departs</p>
              <p className="font-semibold text-zinc-900">{data.departure_time}</p>
            </div>
            <div className="bg-white rounded-lg p-2 border border-zinc-100">
              <p className="text-xs text-zinc-400">Seats Left</p>
              <p className="font-semibold text-zinc-900">{data.available_seats}</p>
            </div>
            <div className="bg-white rounded-lg p-2 border border-zinc-100 col-span-2">
              <p className="text-xs text-zinc-400">Price/Seat</p>
              <p className="font-bold text-zinc-900">${data.price_per_seat?.toFixed(2)}</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ControlTower;
